import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, ChevronUp, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { payrollApi } from "../../services/payrollApi";
import { usePermissions } from "../../hooks/usePermissions";
import { StatusBadge } from "../../components/StatusBadge";
import { PageHelpBanner } from "../../components/PageHelpBanner";
import { PayrollLineListRail } from "../../components/payroll/PayrollLineListRail";
import { PayrollSourceLineage } from "../../components/payroll/PayrollSourceLineage";
import { NotFoundState } from "../../components/NotFoundState";
import { ReasonDialog } from "../../components/ReasonDialog";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Skeleton } from "../../components/ui/skeleton";
import { formatApiErrorDetail } from "../../lib/api";
import { useLocalization } from "../../hooks/useLocalization";

const ADJUSTMENT_TYPES = [
  "HOURLY_DIFFERENTIAL", "SHIFT_PREMIUM", "OPENING_SHIFT_BONUS", "CLOSING_SHIFT_BONUS",
  "PERFORMANCE_BONUS", "ATTENDANCE_BONUS", "TRANSPORTATION_ALLOWANCE", "FIXED_ALLOWANCE",
  "VARIABLE_ALLOWANCE", "REIMBURSEMENT", "PRIOR_PERIOD_ADJUSTMENT", "OTHER_EARNING", "OTHER_DEDUCTION",
];

export default function PayrollLineDetailPage() {
  const { lineId } = useParams();
  const navigate = useNavigate();
  const { t } = useLocalization();
  const { canPreparePayroll, canReviewPayroll, canReviewTimecards, canPublishPayroll } = usePermissions();
  const [line, setLine] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [period, setPeriod] = useState(null);
  const [siblingLines, setSiblingLines] = useState([]);
  const [disputes, setDisputes] = useState([]);
  const [form, setForm] = useState({ adjustment_type: "OTHER_EARNING", amount: "", reason: "", authorization_note: "" });
  const [rejectAdjustmentId, setRejectAdjustmentId] = useState(null);
  const [disputeAction, setDisputeAction] = useState(null); // { id, status: "RESOLVED"|"REJECTED" }

  const load = useCallback(() => {
    payrollApi.getLine(lineId).then((r) => {
      setLine(r.data);
      setNotFound(false);
      payrollApi.getPeriod(r.data.period_id).then((pr) => setPeriod(pr.data)).catch(() => {});
      payrollApi.listLines(r.data.period_id).then((lr) => setSiblingLines(lr.data)).catch(() => {});
      payrollApi.listDisputes(r.data.period_id).then((dr) => setDisputes(dr.data.filter((d) => d.line_id === lineId))).catch(() => {});
    }).catch((err) => {
      if (err.response?.status === 404) setNotFound(true);
    });
  }, [lineId]);
  useEffect(() => { load(); }, [load]);

  // Up/Down keyboard navigation across sibling employees in this period (audit item 6).
  useEffect(() => {
    const handler = (e) => {
      if (!siblingLines.length || !line) return;
      if (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA" || e.target.tagName === "SELECT") return;
      if (e.key !== "ArrowUp" && e.key !== "ArrowDown") return;
      const idx = siblingLines.findIndex((l) => l.id === line.id);
      const nextIdx = e.key === "ArrowDown" ? Math.min(idx + 1, siblingLines.length - 1) : Math.max(idx - 1, 0);
      if (nextIdx !== idx) { e.preventDefault(); navigate(`/management/payroll/lines/${siblingLines[nextIdx].id}?period=${line.period_id}`); }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [siblingLines, line, navigate]);

  if (notFound) return <NotFoundState testId="payroll-line-not-found" backTo="/management/payroll" />;
  if (!line) return <Skeleton className="h-64 w-full rounded-xl" data-testid="payroll-line-loading" />;

  const isNegative = Number(form.amount) < 0;
  const currentIdx = siblingLines.findIndex((l) => l.id === line.id);

  const submitAdjustment = async () => {
    if (!form.amount || !form.reason) { toast.error(t("mgmtPayrollLine.amountReasonRequired")); return; }
    if (isNegative && !form.authorization_note) { toast.error(t("mgmtPayrollLine.negativeDeductionAuthRequired")); return; }
    try {
      await payrollApi.addAdjustment(lineId, { employee_id: line.employee_id, ...form, authorization_note: isNegative ? form.authorization_note : undefined });
      toast.success(t("mgmtPayrollLine.adjustmentProposed"));
      setForm({ adjustment_type: "OTHER_EARNING", amount: "", reason: "", authorization_note: "" });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const approve = async (adjustmentId) => {
    try { await payrollApi.approveAdjustment(lineId, adjustmentId); toast.success(t("mgmtPayrollLine.adjustmentApproved")); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const rejectingAdjustment = (line.adjustments || []).find((a) => a.id === rejectAdjustmentId);
  const actioningDispute = disputes.find((d) => d.id === disputeAction?.id);

  return (
    <div className="space-y-3" data-testid="payroll-line-detail-page">
      <PageHelpBanner pageKey="payroll-line-detail" title={t("pageHelp.defaultTitle")}>
        <p>{t("mgmtPayrollLine.help")}</p>
      </PageHelpBanner>
      <button data-testid="back-to-period-btn" onClick={() => navigate(`/management/payroll/periods/${line.period_id}`)} className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700">
        <ArrowLeft size={15} /> {t("mgmtPayrollLine.backToPeriod")}
      </button>

      <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-4 items-start" style={{ minHeight: "70vh" }}>
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden lg:sticky lg:top-4 lg:h-[75vh]">
          <PayrollLineListRail
            lines={siblingLines}
            selectedId={line.id}
            onSelect={(id) => navigate(`/management/payroll/lines/${id}?period=${line.period_id}`)}
            testId="payroll-line-rail"
          />
        </div>

        <div className="space-y-4 min-w-0">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-2xl font-bold tracking-tight text-gray-900">{line.employee_name}</h2>
                {siblingLines.length > 0 && <span className="text-xs text-gray-500" data-testid="line-position-indicator">{currentIdx + 1} {t("mgmtDataQuality.of")} {siblingLines.length}</span>}
              </div>
              <p className="text-sm text-gray-500">{line.store_location || "-"}{line.position ? ` · ${line.position}` : ""} · {t("mgmtPayrollLine.grossPay")}: ¥{Number(line.gross_pay).toLocaleString()}</p>
            </div>
            <div className="flex items-center gap-2">
              {line.summary_report_path && (
                <a data-testid="view-summary-payslip-link" href={payrollApi.getLineReportUrl(line.id, "summary")} target="_blank" rel="noreferrer" className="px-3 py-1.5 rounded-full text-xs font-semibold border border-gray-200 hover:bg-gray-50">{t("mgmtPayrollLine.viewSummaryPayslip")}</a>
              )}
              {line.detailed_report_path && (
                <a data-testid="view-detailed-payslip-link" href={payrollApi.getLineReportUrl(line.id, "detailed")} target="_blank" rel="noreferrer" className="px-3 py-1.5 rounded-full text-xs font-semibold border border-gray-200 hover:bg-gray-50">{t("mgmtPayrollLine.viewDetailedPayslip")}</a>
              )}
              <Button size="icon" variant="outline" data-testid="prev-employee-btn" disabled={currentIdx <= 0} onClick={() => navigate(`/management/payroll/lines/${siblingLines[currentIdx - 1].id}?period=${line.period_id}`)}><ChevronUp size={15} /></Button>
              <Button size="icon" variant="outline" data-testid="next-employee-btn" disabled={currentIdx < 0 || currentIdx >= siblingLines.length - 1} onClick={() => navigate(`/management/payroll/lines/${siblingLines[currentIdx + 1].id}?period=${line.period_id}`)}><ChevronDown size={15} /></Button>
              <StatusBadge status={line.status} />
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-xl p-4 grid grid-cols-3 gap-3 text-sm">
            <p><span className="text-gray-500">{t("mgmtPayroll.colRegularHrs")}:</span> {line.regular_hours}</p>
            <p><span className="text-gray-500">{t("mgmtPayroll.colLateNightHrs")}:</span> {line.late_night_hours}</p>
            <p><span className="text-gray-500">{t("mgmtPayroll.transportation")}:</span> ¥{line.transportation_allowance}</p>
          </div>

          {period && <PayrollSourceLineage line={line} period={period} canOpenTimecard={canReviewTimecards} testId="payroll-source-lineage" />}

          {disputes.length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4" data-testid="payslip-disputes-panel">
              <p className="text-xs uppercase tracking-[0.2em] text-red-700 mb-2">{t("mgmtPayrollLine.payslipDisputes")}</p>
              <div className="divide-y divide-red-200">
                {disputes.map((d) => (
                  <div key={d.id} className="py-2.5 flex items-start justify-between gap-3" data-testid={`dispute-row-${d.id}`}>
                    <div className="min-w-0">
                      <p className="text-sm text-red-900">"{d.description}"</p>
                      <p className="text-xs text-red-600 mt-0.5">{t("mgmtPayrollLine.submitted")} {new Date(d.created_at).toLocaleDateString()}{d.resolution_notes ? ` · ${t("mgmtPayrollLine.resolution")}: ${d.resolution_notes}` : ""}</p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <StatusBadge status={d.status} />
                      {canPublishPayroll && d.status === "OPEN" && (
                        <>
                          <Button size="sm" data-testid={`resolve-dispute-btn-${d.id}`} onClick={() => setDisputeAction({ id: d.id, status: "RESOLVED" })} className="bg-emerald-600 hover:bg-emerald-700">{t("mgmtPayrollLine.resolve")}</Button>
                          <Button size="sm" variant="outline" data-testid={`reject-dispute-btn-${d.id}`} onClick={() => setDisputeAction({ id: d.id, status: "REJECTED" })} className="text-red-600 border-red-200 hover:bg-red-50">{t("common.reject")}</Button>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {(line.premium_breakdown || []).length > 0 && (
            <div className="bg-white border border-gray-200 rounded-xl p-4" data-testid="premium-breakdown-panel">
              <p className="text-xs uppercase tracking-[0.2em] text-gray-500 mb-2">{t("mgmtPayrollLine.premiumPayBreakdown")}</p>
              <div className="divide-y divide-gray-100">
                {line.premium_breakdown.map((p, i) => (
                  <div key={i} className="py-2 flex items-center justify-between text-sm" data-testid={`premium-breakdown-row-${i}`}>
                    <div>
                      <p className="font-semibold text-gray-800">{p.rule_name}</p>
                      <p className="text-xs text-gray-500">{p.classification.replaceAll("_", " ")} · {p.qualifying_hours} {t("mgmtPayrollLine.hrsAbbrev")} · {p.rate_percent}%</p>
                    </div>
                    <p className="font-semibold text-emerald-600">¥{Number(p.amount).toLocaleString()}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {(line.shift_premium_review_flags || []).length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4" data-testid="shift-premium-review-panel">
              <p className="text-xs uppercase tracking-[0.2em] text-amber-700 mb-2">{t("mgmtPayrollLine.shiftPremiumReviewRequired")}</p>
              <p className="text-xs text-amber-700 mb-3">{t("mgmtPayrollLine.shiftPremiumReviewHelp")}</p>
              <div className="divide-y divide-amber-200">
                {line.shift_premium_review_flags.map((f, i) => (
                  <div key={i} className="py-1.5 text-sm text-amber-800" data-testid={`shift-premium-review-row-${i}`}>
                    {f.date} - {t("mgmtPayrollLine.worked")} {f.worked_minutes} {t("mgmtPayrollLine.min")}, {f.shortfall_minutes} {t("mgmtPayrollLine.minShortOfFullShift")}
                  </div>
                ))}
              </div>
            </div>
          )}

          {canPreparePayroll && (
            <div className="bg-white border border-gray-200 rounded-xl p-4 space-y-3" data-testid="add-adjustment-form">
              <p className="text-xs uppercase tracking-[0.2em] text-gray-500">{t("mgmtPayrollLine.proposeAdjustment")}</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Select value={form.adjustment_type} onValueChange={(v) => setForm({ ...form, adjustment_type: v })}>
                  <SelectTrigger data-testid="adjustment-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{ADJUSTMENT_TYPES.map((at) => <SelectItem key={at} value={at}>{at.replaceAll("_", " ")}</SelectItem>)}</SelectContent>
                </Select>
                <Input data-testid="adjustment-amount-input" type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} placeholder={t("mgmtPayrollLine.amountJpyPlaceholder")} />
                <Input data-testid="adjustment-reason-input" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder={t("mgmtPayrollLine.reasonPlaceholder")} />
                <Button data-testid="submit-adjustment-btn" onClick={submitAdjustment}>{t("mgmtPayrollLine.propose")}</Button>
              </div>
              {isNegative && (
                <Input data-testid="adjustment-authorization-input" value={form.authorization_note} onChange={(e) => setForm({ ...form, authorization_note: e.target.value })} placeholder={t("mgmtPayrollLine.authorizationNotePlaceholder")} className="border-red-200" />
              )}
            </div>
          )}

          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <p className="px-4 py-3 text-xs uppercase tracking-[0.2em] text-gray-500 border-b border-gray-100">{t("mgmtPayrollLine.adjustments")}</p>
            <div className="divide-y divide-gray-100">
              {(line.adjustments || []).map((a) => (
                <div key={a.id} className="p-4 flex items-center justify-between" data-testid={`adjustment-row-${a.id}`}>
                  <div>
                    <p className="text-sm font-semibold text-gray-800">{a.adjustment_type.replaceAll("_", " ")} - <span className={Number(a.amount) < 0 ? "text-red-600" : "text-emerald-600"}>¥{a.amount}</span></p>
                    <p className="text-xs text-gray-500">{a.reason}{a.authorization_note ? ` · ${t("mgmtPayrollLine.authorization")}: ${a.authorization_note}` : ""}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <StatusBadge status={a.status} />
                    {canReviewPayroll && a.status === "PENDING" && (
                      <>
                        <Button size="sm" data-testid={`approve-adjustment-btn-${a.id}`} onClick={() => approve(a.id)} className="bg-emerald-600 hover:bg-emerald-700">{t("common.approve")}</Button>
                        <Button size="sm" variant="outline" data-testid={`reject-adjustment-btn-${a.id}`} onClick={() => setRejectAdjustmentId(a.id)} className="text-red-600 border-red-200 hover:bg-red-50">{t("common.reject")}</Button>
                      </>
                    )}
                  </div>
                </div>
              ))}
              {(line.adjustments || []).length === 0 && <p className="p-6 text-center text-sm text-gray-500" data-testid="no-adjustments-row">{t("mgmtPayrollLine.noAdjustmentsProposed")}</p>}
            </div>
          </div>
        </div>
      </div>

      {rejectingAdjustment && (
        <ReasonDialog
          open onOpenChange={(v) => !v && setRejectAdjustmentId(null)} title={t("mgmtPayrollLine.rejectAdjustment")}
          contextItems={[
            { label: t("mgmtPayroll.employeeName"), value: line.employee_name },
            { label: t("mgmtPayrollLine.adjustmentType"), value: rejectingAdjustment.adjustment_type.replaceAll("_", " ") },
            { label: t("mgmtPayroll.amount"), value: `¥${rejectingAdjustment.amount}` },
            { label: t("mgmtPayrollLine.proposedReason"), value: rejectingAdjustment.reason },
          ]}
          reasonLabel={t("mgmtPayrollLine.rejectionReason")} danger confirmLabel={t("mgmtPayrollLine.rejectAdjustment")}
          onConfirm={async (reason) => { await payrollApi.rejectAdjustment(lineId, rejectingAdjustment.id, reason); toast.success(t("mgmtPayrollLine.adjustmentRejected")); load(); }}
          testId="reject-adjustment-dialog"
        />
      )}

      {actioningDispute && (
        <ReasonDialog
          open onOpenChange={(v) => !v && setDisputeAction(null)}
          title={disputeAction.status === "RESOLVED" ? t("mgmtPayrollLine.resolveDispute") : t("mgmtPayrollLine.rejectDispute")}
          contextItems={[
            { label: t("mgmtPayroll.employeeName"), value: line.employee_name },
            { label: t("mgmtPayrollLine.dispute"), value: actioningDispute.description },
          ]}
          reasonLabel={t("mgmtPayrollLine.resolutionNotes")} reasonPlaceholder={t("mgmtPayrollLine.resolutionNotesPlaceholder")}
          danger={disputeAction.status === "REJECTED"} confirmLabel={disputeAction.status === "RESOLVED" ? t("mgmtPayrollLine.markResolved") : t("mgmtPayrollLine.rejectDispute")}
          onConfirm={async (notes) => {
            await payrollApi.resolveDispute(actioningDispute.id, { status: disputeAction.status, resolution_notes: notes });
            toast.success(disputeAction.status === "RESOLVED" ? t("mgmtPayrollLine.disputeResolvedNotified") : t("mgmtPayrollLine.disputeRejectedNotified"));
            setDisputeAction(null); load();
          }}
          testId="resolve-dispute-dialog"
        />
      )}
    </div>
  );
}
